import * as THREE from 'three';

export interface OriginalMeshData {
  mesh: THREE.Mesh;
  material: THREE.Material | THREE.Material[];
  hadVertexColors: boolean;
}

/** Find the map model tagged with isMapModel in the scene */
export function findMapModel(scene: THREE.Scene): THREE.Object3D | null {
  for (const child of scene.children) {
    if (child.userData.isMapModel) return child;
  }
  return null;
}

/** Restore original materials and remove vertex color attributes */
export function restoreOriginals(originals: OriginalMeshData[]): void {
  for (const {mesh, material, hadVertexColors} of originals) {
    if (Array.isArray(mesh.material)) {
      for (const m of mesh.material) m.dispose();
    } else {
      mesh.material.dispose();
    }
    mesh.material = material as any;
    if (!hadVertexColors && mesh.geometry.attributes.color) {
      mesh.geometry.deleteAttribute('color');
    }
  }
}

/**
 * Paint vertex colors on a map model.
 * colorFn receives a world-space vertex position and returns either
 * a color {r,g,b} to paint, or null to leave as neutral gray.
 */
export function paintModelVertexColors(
  mapModel: THREE.Object3D,
  colorFn: (worldX: number, worldY: number, worldZ: number) => {r: number; g: number; b: number} | null,
): OriginalMeshData[] {
  const originals: OriginalMeshData[] = [];
  const tempPos = new THREE.Vector3();

  mapModel.traverse((obj: THREE.Object3D) => {
    if (!(obj instanceof THREE.Mesh)) return;
    const mesh = obj as THREE.Mesh;
    const geometry = mesh.geometry;
    if (!geometry || !geometry.attributes.position) return;

    const posAttr = geometry.attributes.position;
    const vertexCount = posAttr.count;

    // Store originals before modifying
    originals.push({
      mesh,
      material: Array.isArray(mesh.material) ? mesh.material.map(m => m) : mesh.material,
      hadVertexColors: !!(geometry.attributes.color),
    });

    const colors = new Float32Array(vertexCount * 3);
    let hasAnyPainted = false;

    mesh.updateWorldMatrix(true, false);
    const worldMatrix = mesh.matrixWorld;

    for (let i = 0; i < vertexCount; i++) {
      tempPos.set(posAttr.getX(i), posAttr.getY(i), posAttr.getZ(i));
      tempPos.applyMatrix4(worldMatrix);

      const result = colorFn(tempPos.x, tempPos.y, tempPos.z);
      if (result) {
        colors[i * 3] = result.r;
        colors[i * 3 + 1] = result.g;
        colors[i * 3 + 2] = result.b;
        hasAnyPainted = true;
      } else {
        colors[i * 3] = 1.0;
        colors[i * 3 + 1] = 1.0;
        colors[i * 3 + 2] = 1.0;
      }
    }

    if (!hasAnyPainted) return;

    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const applyVC = (mat: THREE.Material): THREE.Material => {
      const cloned = mat.clone();
      (cloned as any).vertexColors = true;
      // Reset tint to white so vertex colors aren't multiplied with it
      // (GLBLoader tints the map copper; without this, heatmap colors
      // get darkened into near-invisibility).
      const matColor = (cloned as any).color
      if (matColor && typeof matColor.set === 'function') {
        matColor.set(0xffffff)
      }
      cloned.needsUpdate = true;
      return cloned;
    };

    if (Array.isArray(mesh.material)) {
      mesh.material = mesh.material.map(applyVC);
    } else {
      mesh.material = applyVC(mesh.material);
    }
  });

  return originals;
}

/** Distance from point (px,pz) to line segment (ax,az)-(bx,bz) in the X/Z plane */
export function distanceToSegmentXZ(
  px: number, pz: number,
  ax: number, az: number,
  bx: number, bz: number,
): number {
  const dx = bx - ax;
  const dz = bz - az;
  const lenSq = dx * dx + dz * dz;
  if (lenSq === 0) {
    const ex = px - ax;
    const ez = pz - az;
    return Math.sqrt(ex * ex + ez * ez);
  }
  let t = ((px - ax) * dx + (pz - az) * dz) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const projX = ax + t * dx;
  const projZ = az + t * dz;
  const ex = px - projX;
  const ez = pz - projZ;
  return Math.sqrt(ex * ex + ez * ez);
}

/** Min distance from point to a polyline in X/Z plane */
export function minDistanceToPolylineXZ(
  px: number, pz: number,
  polyline: Array<{x: number; z: number}>,
): number {
  let minDist = Infinity;
  for (let i = 0; i < polyline.length - 1; i++) {
    const dist = distanceToSegmentXZ(
      px, pz,
      polyline[i].x, polyline[i].z,
      polyline[i + 1].x, polyline[i + 1].z,
    );
    if (dist < minDist) minDist = dist;
  }
  return minDist;
}

/**
 * Async version of paintModelVertexColors — processes vertices in chunks
 * to avoid blocking the main thread. Accepts an AbortSignal for cancellation.
 * Colors are only applied to meshes after all vertices are processed.
 */
const CHUNK_SIZE = 30000;

export function paintModelVertexColorsAsync(
  mapModel: THREE.Object3D,
  colorFn: (worldX: number, worldY: number, worldZ: number) => {r: number; g: number; b: number} | null,
  signal?: AbortSignal,
): Promise<OriginalMeshData[]> {
  return new Promise(resolve => {
    const originals: OriginalMeshData[] = [];
    const tempPos = new THREE.Vector3();

    // Collect meshes and prepare color buffers
    const meshWork: Array<{
      mesh: THREE.Mesh;
      posAttr: THREE.BufferAttribute;
      worldMatrix: THREE.Matrix4;
      colors: Float32Array;
      count: number;
      hasPainted: boolean;
    }> = [];

    mapModel.traverse((obj: THREE.Object3D) => {
      if (!(obj instanceof THREE.Mesh)) return;
      const mesh = obj as THREE.Mesh;
      const geometry = mesh.geometry;
      if (!geometry || !geometry.attributes.position) return;

      const posAttr = geometry.attributes.position as THREE.BufferAttribute;
      mesh.updateWorldMatrix(true, false);

      originals.push({
        mesh,
        material: Array.isArray(mesh.material) ? mesh.material.map(m => m) : mesh.material,
        hadVertexColors: !!(geometry.attributes.color),
      });

      meshWork.push({
        mesh,
        posAttr,
        worldMatrix: mesh.matrixWorld.clone(),
        colors: new Float32Array(posAttr.count * 3),
        count: posAttr.count,
        hasPainted: false,
      });
    });

    let mIdx = 0;
    let vIdx = 0;

    function processChunk() {
      if (signal?.aborted) {
        resolve([]);
        return;
      }

      let processed = 0;
      while (mIdx < meshWork.length && processed < CHUNK_SIZE) {
        const w = meshWork[mIdx];
        while (vIdx < w.count && processed < CHUNK_SIZE) {
          tempPos.set(
            w.posAttr.getX(vIdx),
            w.posAttr.getY(vIdx),
            w.posAttr.getZ(vIdx),
          );
          tempPos.applyMatrix4(w.worldMatrix);

          const result = colorFn(tempPos.x, tempPos.y, tempPos.z);
          if (result) {
            w.colors[vIdx * 3] = result.r;
            w.colors[vIdx * 3 + 1] = result.g;
            w.colors[vIdx * 3 + 2] = result.b;
            w.hasPainted = true;
          } else {
            w.colors[vIdx * 3] = 1.0;
            w.colors[vIdx * 3 + 1] = 1.0;
            w.colors[vIdx * 3 + 2] = 1.0;
          }
          vIdx++;
          processed++;
        }

        if (vIdx >= w.count) {
          mIdx++;
          vIdx = 0;
        }
      }

      if (mIdx < meshWork.length) {
        setTimeout(processChunk, 0);
      } else {
        // Done — apply colors to meshes
        if (signal?.aborted) { resolve([]); return; }

        for (const w of meshWork) {
          if (!w.hasPainted) continue;
          w.mesh.geometry.setAttribute('color', new THREE.BufferAttribute(w.colors, 3));

          const applyVC = (mat: THREE.Material): THREE.Material => {
            const cloned = mat.clone();
            (cloned as any).vertexColors = true;
            // Reset tint to white so vertex colors aren't multiplied with it
            // (GLBLoader tints the map copper; without this, heatmap colors
            // get darkened into near-invisibility).
            const matColor = (cloned as any).color
            if (matColor && typeof matColor.set === 'function') {
              matColor.set(0xffffff)
            }
            cloned.needsUpdate = true;
            return cloned;
          };

          if (Array.isArray(w.mesh.material)) {
            w.mesh.material = w.mesh.material.map(applyVC);
          } else {
            w.mesh.material = applyVC(w.mesh.material);
          }
        }
        resolve(originals);
      }
    }

    processChunk();
  });
}
